// src/features/clients/components/ClientSelect/index.jsx
import React, { useMemo } from 'react';
import Select from 'react-select';
import { useAuth } from '../../../auth/context/AuthContext';

function ClientSelect({ clients, value, onChange, onAddNew, isLoading, placeholder }) {
  const { hasPermission } = useAuth();

  const options = useMemo(() =>
    clients
      .filter(Boolean)
      .map(client => ({
        value: client.id,
        label: `${client.name}${client.phone ? ` (${client.phone})` : ''}`,
      })),
  [clients]);

  const selectedValue = options.find(opt => opt.value === value);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '45px',
      borderColor: 'var(--border-default)',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--font-size-sm)',
      background: 'var(--bg-muted)',
      '&:hover': { borderColor: 'var(--border-strong)' },
    }),
    container: (base) => ({ ...base, flexGrow: 1 }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-default)',
      boxShadow: 'var(--shadow-lg)',
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    option: (provided, state) => ({
      ...provided,
      fontSize: 'var(--font-size-sm)',
      background: state.isSelected
        ? 'var(--color-primary)'
        : state.isFocused
          ? 'var(--color-primary-subtle)'
          : 'transparent',
      color: state.isSelected ? 'var(--text-on-primary)' : 'var(--text-primary)',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--text-primary)',
      fontSize: 'var(--font-size-sm)',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--text-placeholder)',
      fontSize: 'var(--font-size-sm)',
    }),
    menuList: (provided) => ({ ...provided, maxHeight: 500 }),
  };

  const customFilter = (option, inputValue) => {
    if (!inputValue) return true;
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
  };

  if (!hasPermission('view_clients')) {
    return (
      <div style={{
        padding:         'var(--space-3) var(--space-4)',
        border:          '1px solid var(--border-default)',
        borderRadius:    'var(--radius-md)',
        background:      'var(--bg-muted)',
        color:           'var(--text-muted)',
        fontSize:        'var(--font-size-sm)',
      }}>
        لا تملك صلاحية عرض العملاء.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', position: 'relative' }}>
      <Select
        styles={customStyles}
        options={options}
        value={selectedValue || null}
        onChange={(selected) => onChange(selected ? selected.value : '')}
        placeholder={placeholder || 'ابحث أو اختر عميلاً...'}
        isLoading={isLoading}
        isClearable
        isRtl
        maxMenuHeight={500}
        noOptionsMessage={() => 'لم يتم العثور على عملاء'}
        loadingMessage={() => 'جاري التحميل...'}
        filterOption={customFilter}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />

      {hasPermission('add_client') && onAddNew && (
        <button
          type="button"
          onClick={onAddNew}
          style={{
            flexShrink:    0,
            width:         '45px',
            height:        '45px',
            fontSize:      '1.5rem',
            background:    'var(--color-primary)',
            color:         'var(--text-on-primary)',
            border:        'none',
            borderRadius:  'var(--radius-md)',
            cursor:        'pointer',
            display:       'flex',
            alignItems:    'center',
            justifyContent:'center',
            boxShadow:     'var(--shadow-primary)',
            transition:    'var(--transition-fast)',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--color-primary-hover)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--color-primary)'}
        >
          +
        </button>
      )}
    </div>
  );
}

export default ClientSelect;